import { useEffect, useMemo, useRef } from 'react'

import { contentFor } from '../../docs/content.ts'
import { Markdown } from '../../docs/Markdown.tsx'
import {
  pagesOf,
  pathOf,
  type DocPage,
  type DocTree,
} from '../../docs/manifest.ts'
import { headingsOf } from '../../docs/toc.ts'
import { useMediaQuery } from '../../docs/useMediaQuery.ts'
import { navigateOnPlainClick } from '../../shell/links.ts'
import './docs.css'

// A documentation page: page tree, article, on-page contents.
//
// The tree lives in this pane rather than in the shell's rail. That follows the
// rail's existing rule (see shell/Rail.tsx) that a second nav column belongs to
// the screen that needs it, not to the chrome — the same reason Scalar's endpoint
// tree sits inside the reference pane.

// Below this the tree stops being a column and becomes a disclosure above the
// article. Matches the breakpoint in docs.css.
export const TREE_COLLAPSE_QUERY = '(max-width: 996px)'

const TreeNav = ({
  tree,
  current,
  onNavigate,
}: {
  tree: DocTree
  current: DocPage
  onNavigate: (path: string) => void
}) => (
  <nav aria-label={`${tree.label} documentation`} className="curri-docs__tree">
    {tree.groups.map(group => {
      // Unlisted pages never appear here; a group of only unlisted pages
      // therefore renders nothing at all rather than an empty heading.
      const visible = group.pages.filter(page => !page.unlisted)
      if (visible.length === 0) return null

      return (
        <div className="curri-docs__group" key={group.label}>
          <p className="curri-docs__group-label">{group.label}</p>
          {visible.map(page => {
            const path = pathOf(tree, page)
            const active = page.slug === current.slug
            return (
              <a
                aria-current={active ? 'page' : undefined}
                className={`curri-docs__link${active ? ' is-active' : ''}`}
                href={path}
                key={page.slug || 'index'}
                onClick={navigateOnPlainClick(onNavigate, path)}
              >
                {page.title}
              </a>
            )
          })}
        </div>
      )
    })}
  </nav>
)

const Contents = ({ markdown }: { markdown: string }) => {
  // h2/h3 only. h1 is the page title, and h4+ in these docs are field names
  // inside argument tables — listing them produces a contents panel longer than
  // the article.
  const headings = useMemo(
    () => headingsOf(markdown).filter(heading => heading.depth === 2 || heading.depth === 3),
    [markdown]
  )

  if (headings.length < 2) return null

  return (
    <nav aria-label="On this page" className="curri-docs__toc">
      <p className="curri-docs__toc-label">On this page</p>
      {headings.map(heading => (
        <a
          className={`curri-docs__toc-link is-depth-${heading.depth}`}
          href={`#${heading.id}`}
          key={heading.id}
        >
          {heading.text}
        </a>
      ))}
    </nav>
  )
}

const Pagination = ({
  tree,
  current,
  onNavigate,
}: {
  tree: DocTree
  current: DocPage
  onNavigate: (path: string) => void
}) => {
  // Sequenced over listed pages only, so prev/next never lands a reader on a
  // page the nav does not admit exists. This replaces the hand-rolled
  // `pagination-nav` blocks the unlisted pages carried in the markdown.
  const listed = pagesOf(tree).filter(page => !page.unlisted)
  const index = listed.findIndex(page => page.slug === current.slug)
  if (index === -1) return null

  const previous = listed[index - 1]
  const next = listed[index + 1]
  if (!previous && !next) return null

  return (
    <div className="curri-docs__pagination">
      {previous ? (
        <a
          className="curri-docs__pagination-link"
          href={pathOf(tree, previous)}
          onClick={navigateOnPlainClick(onNavigate, pathOf(tree, previous))}
        >
          <span className="curri-docs__pagination-label">Previous</span>
          {previous.title}
        </a>
      ) : (
        <span />
      )}
      {next && (
        <a
          className="curri-docs__pagination-link is-next"
          href={pathOf(tree, next)}
          onClick={navigateOnPlainClick(onNavigate, pathOf(tree, next))}
        >
          <span className="curri-docs__pagination-label">Next</span>
          {next.title}
        </a>
      )}
    </div>
  )
}

export const Docs = ({
  tree,
  page,
  onNavigate,
}: {
  tree: DocTree
  page: DocPage
  onNavigate: (path: string) => void
}) => {
  const markdown = useMemo(() => contentFor(page.file), [page.file])
  const article = useRef<HTMLDivElement>(null)
  const path = pathOf(tree, page)
  const collapsedTree = useMediaQuery(TREE_COLLAPSE_QUERY)
  const hasListedPages = useMemo(
    () => pagesOf(tree).some(candidate => !candidate.unlisted),
    [tree]
  )

  // Scroll position on navigation.
  //
  // The shell keys its fade on the section id, which is the same for every doc
  // page, so moving between pages does not remount and the article would
  // otherwise keep the previous page's offset.
  //
  // The `#anchor` case cannot be left to the browser. Native fragment scrolling
  // happens once, at navigation, against markup that React has not rendered yet —
  // and it targets the scrolling ancestor, which here is this inner element
  // rather than the document. Landing on `appendix#deliverymethod` therefore put
  // the reader at the top of a 31,000px page with the heading they asked for far
  // below the fold. That URL is quoted inside Eclipse ERP installs, so this runs
  // after render and does the scroll itself.
  useEffect(() => {
    const hash = window.location.hash.slice(1)
    const target = hash
      ? // `getElementById` rather than `querySelector('#' + hash)`: these ids come
        // from heading text and are not guaranteed to be valid CSS selectors.
        document.getElementById(decodeURIComponent(hash))
      : null

    if (target) {
      target.scrollIntoView()
      return
    }

    // `scrollTop`, not `scrollTo({ top: 0 })` — the latter is not implemented on
    // elements in jsdom, so the difference is a thrown TypeError in the shell's
    // own tests rather than a missed scroll. Nothing here needs smooth scrolling.
    if (article.current) article.current.scrollTop = 0
  }, [path])

  return (
    <div className="curri-docs">
      {/* A column on a wide viewport, a closed disclosure on a narrow one.
          Hiding it outright below the breakpoint left a narrow reader with no way
          to reach any other page — the rail only reaches the tree's index.

          Omitted entirely for a tree with nothing listed (Eclipse, DSP): the nav
          would render empty and still hold its 232px column, which reads as a
          rendering failure rather than as "this tree has no public index". */}
      {hasListedPages && (
        <details className="curri-docs__tree-disclosure" open={!collapsedTree}>
          <summary className="curri-docs__tree-summary">
            {tree.label} documentation
          </summary>
          <TreeNav current={page} onNavigate={onNavigate} tree={tree} />
        </details>
      )}
      <div className="curri-docs__main" ref={article}>
        <article className="curri-docs__article">
          {page.unlisted && (
            // Same warning Docusaurus showed on these pages. It is load-bearing
            // context: someone who arrives here followed a link they were given
            // directly, and should know the page is not part of the public nav.
            <aside className="curri-admonition curri-admonition--warning">
              <p className="curri-admonition__title">Unlisted page</p>
              <p>
                This page is not linked from the documentation nav. It is
                reachable only by direct link, and search engines are asked not
                to index it.
              </p>
            </aside>
          )}
          {/* Docusaurus rendered the frontmatter title as the page heading, and
              most of these pages have no `h1` of their own as a result. Dropping
              it would leave 32 of them untitled. */}
          {!page.hideTitle && <h1>{page.title}</h1>}
          <Markdown
            markdown={markdown}
            onNavigate={onNavigate}
            pagePath={path}
          />
          <Pagination current={page} onNavigate={onNavigate} tree={tree} />
        </article>
        <Contents markdown={markdown} />
      </div>
    </div>
  )
}
